<script setup>
import {onMounted, ref, reactive} from 'vue'
import {ElMessage, ElLoading} from 'element-plus'
import {UserFilled} from '@element-plus/icons-vue'
import VerifyCodeButton from '@/components/templates/VerifyCodeButton.vue'
import apiClient from '@/components/plugins/axios'
import axios from 'axios'

const userInfo = ref({
  nickname: '用户',
  telephone: '',
  gender: '',
  birthday: '',
  avatar: '',
})

// 当前模式：normal, changePhone
const currentMode = ref('normal')

// 修改手机号表单数据
const changePhoneForm = reactive({
  phone: '',
  verifyCode: ''
})

const avatarFileInput = ref(null)

function handleAvatarClick() {
  avatarFileInput.value?.click()
}

async function handleAvatarChange(event) {
  const file = event.target.files?.[0]
  if (!file) return

  // 检查文件类型
  if (!file.type.startsWith('image/')) {
    ElMessage.error('请选择图片文件')
    event.target.value = ''
    return
  }

  // 检查文件大小（限制为5MB）
  if (file.size > 5 * 1024 * 1024) {
    ElMessage.error('图片大小不能超过5MB')
    event.target.value = ''
    return
  }

  // 显示加载状态
  const loading = ElLoading.service({
    lock: true,
    text: '正在上传头像...',
    background: 'rgba(0, 0, 0, 0.7)'
  })

  try {
    // 先显示预览
    const blobUrl = URL.createObjectURL(new Blob([file], {type: file.type}))
    userInfo.value.avatar = blobUrl

    // 获取OSS签名
    const {data} = await apiClient.get('/api/v1/common/oss/sts-signature_v2?upload_dir=' + 'client/upload/' + file.type.split('/')[0] + '&fileName=' + file.name)

    // 构建上传表单数据
    const formData = new FormData()
    formData.append('key', data.data.key)
    formData.append('policy', data.data.policy)
    formData.append('OSSAccessKeyId', data.data.OSSAccessKeyId)
    formData.append('Signature', data.data.signature)
    formData.append('x-oss-security-token', data.data.sts_token)
    formData.append('success_action_status', data.data.success_action_status)
    formData.append('file', file)

    // 上传到OSS
    await axios.post(data.data.host, formData, {
      headers: {'Content-Type': 'multipart/form-data'}
    })

    // 上传成功，更新头像URL
    const avatarUrl = data.data.host + '/' + data.data.key
    userInfo.value.avatar = avatarUrl

    // 只保存头像
    try {
      const res = await apiClient.post('/api/v1/common/update/userInfo', {
        avatar: avatarUrl
      })
      if (res.data.code === 200) {
        // 更新本地存储
        localStorage.setItem('userInfo', JSON.stringify(userInfo.value))
        ElMessage.success('头像上传成功')
      } else {
        ElMessage.error(res.data.message || '保存失败')
      }
    } catch (saveError) {
      console.error('保存头像失败:', saveError)
      ElMessage.error('头像保存失败')
    }
  } catch (error) {
    console.error('头像上传失败:', error)
    // 上传失败，恢复原来的头像
    try {
      const cached = localStorage.getItem('userInfo')
      if (cached) {
        const parsed = JSON.parse(cached)
        userInfo.value.avatar = parsed.avatar || ''
      } else {
        userInfo.value.avatar = ''
      }
    } catch (e) {
      userInfo.value.avatar = ''
    }
    ElMessage.error('头像上传失败，请稍后重试')
  } finally {
    loading.close()
    // 清空input，以便可以重复选择同一文件
    event.target.value = ''
  }
}

// 切换到修改手机号模式
function switchToChangePhone() {
  currentMode.value = 'changePhone'
  changePhoneForm.phone = ''
  changePhoneForm.verifyCode = ''
}

// 取消修改，返回正常模式
function cancelChange() {
  currentMode.value = 'normal'
}

// 保存个人信息
async function saveUserInfo() {
  try {
    const data = {
      // 性别：始终添加（可以为空字符串）
      gender: userInfo.value.gender,
      // 昵称：不为空字符串时添加
      ...(userInfo.value.nickname && {nickname: userInfo.value.nickname}),
      // 生日：不为空字符串时添加
      ...(userInfo.value.birthday && {birthday: userInfo.value.birthday})
    }

    const res = await apiClient.post('/api/v1/common/update/userInfo', data)
    if (res.data.code === 200) {
      ElMessage.success('保存成功')
      // 更新本地存储
      localStorage.setItem('userInfo', JSON.stringify(userInfo.value))
    } else {
      ElMessage.error(res.data.message || '保存失败')
    }
  } catch (error) {
    console.error('保存个人信息失败:', error)
    ElMessage.error('保存失败，请稍后重试')
  }
}

// 提交修改手机号
async function submitChangePhone() {
  if (!changePhoneForm.phone) {
    ElMessage.error('请输入手机号')
    return
  }
  if (!changePhoneForm.verifyCode) {
    ElMessage.error('请输入验证码')
    return
  }
  try {
    const data = {
      telephone: changePhoneForm.phone,
      verifyCode: changePhoneForm.verifyCode
    }
    const res = await apiClient.post('/api/v1/common/update/telephone', data)
    if (res.data.code === 200) {
      userInfo.value.telephone = changePhoneForm.phone
      // 更新本地存储
      localStorage.setItem('userInfo', JSON.stringify(userInfo.value))
      ElMessage.success('手机号修改成功')
      currentMode.value = 'normal'
    } else {
      ElMessage.error(res.data.message || '修改失败')
    }
  } catch (error) {
    console.error('修改手机号失败:', error)
    ElMessage.error('修改失败，请稍后重试')
  }
}

onMounted(function () {
  try {
    const cached = localStorage.getItem('userInfo')
    if (cached) {
      const parsed = JSON.parse(cached)
      userInfo.value = {
        nickname: parsed.nickname || '用户',
        telephone: parsed.telephone,
        gender: parsed.gender,
        birthday: parsed.birthday,
        avatar: parsed.avatar,
      }
    }
  } catch (e) {
    // ignore parse error, use defaults
  }
})
</script>

<template>
  <div class="container">
    <div class="profile-page">
      <div class="page-header">
        <h1>个人信息</h1>
        <p>管理个人资料与账户安全</p>
      </div>

      <el-card class="main-card" shadow="never" style="width: 30rem;padding: 1rem">
        <div class="card-content">
          <!-- 正常模式 -->
          <template v-if="currentMode === 'normal'">
            <div class="avatar-section">
              <div class="avatar-wrapper" @click="handleAvatarClick">
                <img v-if="userInfo.avatar" :src="userInfo.avatar" alt="avatar" class="avatar">
                <div v-else class="avatar placeholder">
                  <el-icon :size="40">
                    <UserFilled/>
                  </el-icon>
                </div>
                <div class="avatar-overlay">
                  <span>点击上传</span>
                </div>
              </div>
              <input
                  ref="avatarFileInput"
                  type="file"
                  accept="image/*"
                  style="display: none"
                  @change="handleAvatarChange"
              />
            </div>

            <el-form label-position="left" class="info-form" :model="userInfo" label-width="4rem">
              <el-form-item label="昵称">
                <el-input v-model="userInfo.nickname" placeholder="请输入昵称" style="width: 12rem"/>
              </el-form-item>
              <el-form-item label="性别">
                <el-select v-model="userInfo.gender" placeholder="请选择性别" style="width: 12rem">
                  <el-option label="男" value="0"/>
                  <el-option label="女" value="1"/>
                  <el-option label="保密" value=""/>
                </el-select>
              </el-form-item>
              <el-form-item label="生日">
                <el-date-picker
                    v-model="userInfo.birthday"
                    type="date"
                    placeholder="请选择生日"
                    value-format="YYYY-MM-DD"
                    style="width: 12rem"
                />
              </el-form-item>
              <el-form-item label="手机号">
                <el-input v-model="userInfo.telephone" placeholder="请输入手机号" style="width: 12rem" disabled/>
                <el-button type="primary" link size="small" @click="switchToChangePhone">修改</el-button>
              </el-form-item>
            </el-form>
            <div class="card-footer">
              <el-button type="primary" size="small" @click="saveUserInfo">保存</el-button>
            </div>
          </template>

          <!-- 修改手机号模式 -->
          <template v-else-if="currentMode === 'changePhone'">
            <div class="mode-header">
              <h3>修改手机号</h3>
            </div>
            <el-form label-position="left" class="info-form" :model="changePhoneForm" label-width="6rem">
              <el-form-item label="新手机号">
                <el-input v-model="changePhoneForm.phone" placeholder="请输入新手机号" style="width: 14rem"/>
              </el-form-item>
              <el-form-item label="验证码">
                <div style="display: flex; gap: 0.5rem; align-items: center;">
                  <el-input v-model="changePhoneForm.verifyCode" placeholder="请输入验证码" style="width: 6rem"/>
                  <VerifyCodeButton :telephone="changePhoneForm.phone"/>
                </div>
              </el-form-item>
            </el-form>
            <div class="card-footer">
              <el-button size="small" @click="cancelChange">取消</el-button>
              <el-button type="primary" size="small" @click="submitChangePhone">确认修改</el-button>
            </div>
          </template>
        </div>
      </el-card>
    </div>
  </div>
</template>

<style scoped>
.container {
  display: flex;
  padding-bottom: 4rem;
  background: linear-gradient(180deg, #f8fbff 0%, #eef3fb 100%);
  justify-content: center;
}

.main-card {
  margin: auto;
}

.profile-page {
  padding: 24px;
  max-width: 800px;
  margin: 0 auto;
  width: 100%;
}

.page-header {
  text-align: center;
  margin-bottom: 18px;
}

.page-header h1 {
  color: #1890ff;
  margin: 0 0 6px;
  font-size: 26px;
}

.page-header p {
  color: #556173;
  margin: 0;
  font-size: 14px;
}


.card-content {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.avatar-section {
  display: flex;
  justify-content: center;
  width: 100%;
}

.avatar-wrapper {
  position: relative;
  cursor: pointer;
  transition: transform 0.2s ease;
}

.avatar-wrapper:hover {
  transform: scale(1.05);
}

.avatar-wrapper:hover .avatar-overlay {
  opacity: 1;
}

.avatar {
  width: 100px;
  height: 100px;
  border-radius: 50%;
  object-fit: cover;
  border: 4px solid #e6eef7;
  display: block;
}

.avatar.placeholder {
  width: 100px;
  height: 100px;
  border-radius: 50%;
  background: #f5f7fa;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 4px solid #e6eef7;
  color: #909399;
}

.avatar-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.2s ease;
  color: #fff;
  font-size: 14px;
}

.mode-header {
  text-align: center;
  margin-bottom: 24px;
}

.mode-header h3 {
  margin: 0;
  color: #1f2937;
  font-size: 18px;
  font-weight: 600;
}

.card-footer {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 1rem;
  margin-top: 24px;
  width: 100%;
}

.info-form {
  margin: 24px auto;
}

.info-form :deep(.el-form-item) {
  margin-bottom: 20px;
}

.info-form :deep(.el-form-item__label) {
  color: #606266;
  font-weight: 500;
}

.info-form :deep(.el-input) {
  width: 50%;
}

.info-form :deep(.el-select) {
  width: 50%;
}

.info-form :deep(.el-date-editor) {
  width: 50%;
}


</style>